import React, { Component } from 'react';
import { RouteComponentProps, withRouter } from 'react-router';
import { WithTranslation, withTranslation } from 'react-i18next';
import { Moment } from 'moment';
import {
  GridComponent,
  ColumnsDirective,
  ColumnDirective,
  Inject,
  Sort,
  Page,
  Toolbar,
  Resize,
  DetailRow,
  PagerComponent,
  Reorder
} from '@syncfusion/ej2-react-grids';
import { ItemModel, ClickEventArgs } from '@syncfusion/ej2-navigations';
import { Modal } from 'react-bootstrap';

import { ITrackingsGridProps } from '../../../interfaces/props/ITrackingsListProps';
import { ITracking } from '../../../common/model/tracking.model';
import * as gridUtils from '../../../utils/grid.utils';
import * as pagerUtils from '../../../utils/pager.utils';
import * as dateHelpers from '../../../helpers/date.helpers';
import * as userSettings from '../../../utils/userSetting.utils';
import * as arrayUtils from '../../../utils/array.utils';
import { IOrderColumn } from '../../../interfaces/IOrderColumn';
import './trackingsGrid.scss';

interface IIncidence {
  date?: Moment;
  description: string;
}

interface ITrackingModalState {
  incidences: IIncidence[];
  showModal: boolean;
}

type TrackingsGridPropsType = ITrackingsGridProps & RouteComponentProps & WithTranslation;

class TrackingsGrid extends Component<TrackingsGridPropsType, ITrackingModalState> {
  public gridComponent: GridComponent = null;
  public pagerComponent: PagerComponent = null;
  public toolbarOptions: ItemModel[] = [];
  public columns: IOrderColumn[] = null;
  private cachedTrackings: ITracking[] | null = null;
  private cachedSortedTrackings: ITracking[] = [];

  private readonly SAVE_COLUMNS_ACTION_ID = 'saveColumnsTrackings';
  private readonly CLEAN_COLUMNS_ACTION_ID = 'cleanColumnsTrackings';
  private readonly EXPORT_ALL_TRACKINGS_TO_EXCEL_ACTION_ID = 'exportAllTrackingsToExcel';

  public constructor(props: TrackingsGridPropsType) {
    super(props);

    const { i18n } = this.props;

    this.toolbarOptions = [
      {
        tooltipText: i18n.t('grid.customButtons.saveColumns'),
        id: this.SAVE_COLUMNS_ACTION_ID,
        prefixIcon: 'icon icon-app-item-save'
      },
      {
        tooltipText: i18n.t('grid.customButtons.cleanColumns'),
        id: this.CLEAN_COLUMNS_ACTION_ID,
        prefixIcon: 'icon icon-tuning',
        align: 'Right'
      }
    ];

    this.state = {
      incidences: [],
      showModal: false
    };
  }

  public componentDidUpdate(prevProps: TrackingsGridPropsType) {
    gridUtils.manageSpinner(this.gridComponent, prevProps.isFetching, this.props.isFetching);
  }

  public async componentDidMount() {
    const { currentUserId } = this.props;

    if (this.columns === undefined || this.columns == null || this.columns.length === 0) {
      gridUtils.manageSpinner(this.gridComponent, false, true);
      this.columns = gridUtils.getColumnsGridFromLocalStorage(currentUserId, this.gridComponent);

      gridUtils.manageSpinner(this.gridComponent, true, false);
    }
  }

  public onToolbarClick(args: ClickEventArgs) {
    const { currentUserId } = this.props;

    if (args.item.id === this.EXPORT_ALL_TRACKINGS_TO_EXCEL_ACTION_ID) {
      if (this.props.onExportToExcelButtonClick !== undefined && this.props.onExportToExcelButtonClick != null) {
        this.props.onExportToExcelButtonClick();
      }
    } else if (args.item.id === this.SAVE_COLUMNS_ACTION_ID) {
      this.props.onSaveColumnsButtonClick(userSettings.getInfoToSaveUserSetting(currentUserId, gridUtils.getKeyToLocalStorage(currentUserId, this.gridComponent), gridUtils.gridLocalStorageKeySeparator, this.columns));
    } else if (args.item.id === this.CLEAN_COLUMNS_ACTION_ID) {
      this.props.onCleanColumnsButtonClick(userSettings.getKeyToUserSettingFromlocalStorageKey(currentUserId, gridUtils.getKeyToLocalStorage(currentUserId, this.gridComponent), gridUtils.gridLocalStorageKeySeparator));
    }
  }

  public onGridComponentActionComplete(args: any) {
    if (args.requestType === 'refresh') {
      gridUtils.orderColumns(this.props.currentUserId, this.gridComponent);
    } else if (args.requestType === 'reorder') {
      this.onChangeColumns();
    }
  }

  public onChangeColumns() {
    this.columns = gridUtils.mapToLocalStorage(this.gridComponent.columns);
    gridUtils.saveColumnsGridInLocalStorage(this.props.currentUserId, this.gridComponent, this.columns);
  }

  public onGridComponentDataBound() {
    const { currentPage, pageSize, totalCount, searchDate, i18n } = this.props;

    pagerUtils.refreshPager(this.pagerComponent, currentPage, pageSize, totalCount, searchDate, i18n);
  }

  private setIncidences(tracking: ITracking) {
    const { trackings } = this.props;

    const incidences: IIncidence[] = trackings
      .filter((t) =>
        t.reportNumber === tracking.reportNumber &&
        t.version === tracking.version &&
        t.seasonCode === tracking.seasonCode &&
        t.model === tracking.model)
      .map((t) => ({
        date: t.date,
        description: t.description
      }))
      .sort((a, b) => {
        if (!a.date && !b.date) return 0;
        if (!a.date) return 1;
        if (!b.date) return -1;
        return a.date.diff(b.date);
      });

    const uniqueIncidences = incidences.filter((incidence, index, self) =>
      index === self.findIndex((t) =>
        dateHelpers.toShortDate(t.date) === dateHelpers.toShortDate(incidence.date) &&
        t.description === incidence.description
      )
    );

    this.setState({ incidences: uniqueIncidences, showModal: true });
  }

  public getReportNumberColumnTemplate(tracking: ITracking): JSX.Element {
    return (
      <span
        className='e-link'
        onClick={() => {
          this.setIncidences(tracking);
        }}
      >
        {tracking.reportNumber}
      </span>
    );
  }

  public getIncidenceDateColumnTemplate(incidence: IIncidence): JSX.Element {
    return <span>{dateHelpers.toShortDate(incidence.date)}</span>;
  }

  public getShipmentDateColumnTemplate(tracking: ITracking): JSX.Element {
    return <span>{dateHelpers.toShortDate(tracking.shipmentDate)}</span>;
  }

  public onPagerComponentClick(e: any): void {
    if (e.currentPage !== this.props.currentPage) {
      this.props.onCurrentPageChange(e.currentPage);
    }
  }

  public onPageSizesDropdownChange(e: any): void {
    if (e.pageSize !== this.props.pageSize) {
      this.props.onPageSizeChange(e.pageSize);
    }
  }

  public handleClose(): void {
    this.setState({ showModal: false });
  }

  public getUniqueAndSortTrackings(): ITracking[] {
    const { trackings } = this.props;

    if (trackings === this.cachedTrackings) {
      return this.cachedSortedTrackings;
    }

    const uniqueTrackings = trackings.filter((tracking, index, self) =>
      index === self.findIndex((t) =>
        t.reportNumber === tracking.reportNumber &&
        t.version === tracking.version &&
        t.seasonCode === tracking.seasonCode &&
        t.model === tracking.model
      )
    );

    const sortedTrackings = uniqueTrackings.sort((a, b) => {
      const reportNumberComparison = a.reportNumber.localeCompare(b.reportNumber);
      if (reportNumberComparison !== 0) {
        return reportNumberComparison;
      }
      return a.version.localeCompare(b.version);
    });

    this.cachedTrackings = trackings;
    this.cachedSortedTrackings = sortedTrackings;
    return sortedTrackings;
  }

  public render(): JSX.Element {
    const { gridRef, i18n, currentUserId } = this.props;

    const sortedTrackings = this.getUniqueAndSortTrackings();

    const exportToExcelActionIndex = this.toolbarOptions.findIndex((option) => (option).id === this.EXPORT_ALL_TRACKINGS_TO_EXCEL_ACTION_ID);
    if (exportToExcelActionIndex < 0 && this.props.onExportToExcelButtonClick !== undefined && this.props.onExportToExcelButtonClick != null) {
      this.toolbarOptions = arrayUtils.unshiftItem(this.toolbarOptions, this.exportToExcelActionItemModel());
    }

    gridUtils.setWidthToColumns(currentUserId, this.gridComponent, this.columns);

    return (
      <div>
        <div className='trackings-grid'>
          <GridComponent
            created={() => gridUtils.orderColumns(currentUserId, this.gridComponent)}
            dataSource={sortedTrackings}
            toolbar={this.toolbarOptions}
            allowSorting={true}
            allowPaging={true}   /* Si no se incluye este paginador (oculto por css), no se muestra el dropdown en el customizado */
            allowExcelExport={false}
            allowReordering={true}
            allowResizing={true}
            pageSettings={{ pageSize: pagerUtils.getMaxPageSize() }}
            clipMode='EllipsisWithTooltip'
            locale={i18n.language}
            ref={(grid: GridComponent) => { this.gridComponent = grid; gridRef(grid); }}
            toolbarClick={(args) => this.onToolbarClick(args)}
            dataBound={() => this.onGridComponentDataBound()}
            actionComplete={(args) => this.onGridComponentActionComplete(args as any)}
            resizeStop={() => this.onChangeColumns()}
            height='100%'
            width='100%'
            id='trackingsGrid'
          >
            <ColumnsDirective>
              <ColumnDirective
                headerText={i18n.t('trackingsList.reportNumber')}
                field='reportNumber'
                isPrimaryKey={true}
                width='50'
                template={(tracking: ITracking) => this.getReportNumberColumnTemplate(tracking)}
                lockColumn={true}
              />
              <ColumnDirective
                headerText={i18n.t('trackingsList.version')}
                field='version'
                width='40'
                lockColumn={true}
              />
              <ColumnDirective
                headerText={i18n.t('trackingsList.season')}
                field='seasonCode'
                width='50'
              />
              <ColumnDirective
                headerText={i18n.t('trackingsList.model')}
                field='model'
                width='75'
              />
              <ColumnDirective
                headerText={i18n.t('trackingsList.uneco')}
                field='uneco'
                width='75'
              />
              <ColumnDirective
                headerText={i18n.t('trackingsList.division')}
                field='division'
                width='75'
              />
              <ColumnDirective
                headerText={i18n.t('trackingsList.mark')}
                field='mark'
                width='75'
              />
              <ColumnDirective
                headerText={i18n.t('trackingsList.office')}
                field='office'
                width='75'
              />
              <ColumnDirective
                headerText={i18n.t('trackingsList.vendorCode')}
                field='companyVendorCode'
                width='75'
              />
              <ColumnDirective
                headerText={i18n.t('trackingsList.vendor')}
                field='vendor'
                width='75'
              />
              <ColumnDirective
                headerText={i18n.t('trackingsList.shipmentDate')}
                field='shipmentDate'
                width='75'
                template={(tracking: ITracking) => this.getShipmentDateColumnTemplate(tracking)}
              />
            </ColumnsDirective>
            <Inject services={[Sort, Page, Toolbar, Resize, DetailRow, Reorder]} />
          </GridComponent>
          {sortedTrackings.length ?
            <PagerComponent
              pageSize={pagerUtils.getDefaultPageSize()}
              pageCount={pagerUtils.getPageCount()}
              pageSizes={pagerUtils.getPageSizes()}
              ref={(pagerComponent: PagerComponent): PagerComponent => this.pagerComponent = pagerComponent}
              click={(e) => this.onPagerComponentClick(e)}
              dropDownChanged={(e) => this.onPageSizesDropdownChange(e)}
            />
            :
            null
          }
        </div>
        <Modal className='modal-apply' show={this.state.showModal} onHide={() => this.handleClose()}>
          <Modal.Header closeButton={true}>
            <Modal.Title>{i18n.t('trackingsList.title')}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {<GridComponent
              dataSource={this.state.incidences}
              clipMode='EllipsisWithTooltip'
              locale={i18n.language}
              height='100%'
              width='100%'
              id='incidencesGrid'
            >
              <ColumnsDirective>
                <ColumnDirective
                  headerText={i18n.t('trackingsList.date')}
                  field='date'
                  isPrimaryKey={true}
                  width='50'
                  lockColumn={true}
                  template={(incidence: IIncidence) => this.getIncidenceDateColumnTemplate(incidence)}
                />
                <ColumnDirective
                  headerText={i18n.t('trackingsList.description')}
                  field='description'
                  width='200'
                  lockColumn={true}
                />
              </ColumnsDirective>
            </GridComponent>}
          </Modal.Body>
        </Modal>
      </div>
    );
  }

  private exportToExcelActionItemModel(): ItemModel {
    return {
      tooltipText: this.props.i18n.t('trackingsList.actions.exportTrackingsToExcel'),
      id: this.EXPORT_ALL_TRACKINGS_TO_EXCEL_ACTION_ID,
      prefixIcon: 'icon-file-excel-o'
    };
  }
}

export default withRouter(withTranslation()(TrackingsGrid));
